import pptxgen from 'pptxgenjs'
import { formatHTML, getFontFace } from '@/helpers/formatHTMLUtils'
import { toPoints } from '@/helpers/svgPathParser'
import { formatPoints } from '@/helpers/formatPointsUtils'
import { formatColor } from '@/helpers/formatColorUtils'
import tinycolor from 'tinycolor2'
import { getElementRange, getLineElementPath } from '@/helpers/getLinePathUtils'
import { SliderModels } from '@/types/slider'

const dashTypeMap = {
  solid: 'solid',
  dashed: 'dash',
  dotted: 'sysDot',
}
const viewportSize = 1000
// const viewportSize = 640
export const genPx = (designPx: number) => {
  const designWidth = 1920
  return Math.ceil(designPx / (designWidth / viewportSize))
}

export const replaceFontSize = (HTML: string) => {
  return HTML.replace(/font-size: (\d+(\.\d+)?)px/g, (_, p1) => `font-size: ${genPx(Number(p1))}px`)
}
export const ratioPx2Inch = 96 * (viewportSize / 960)
export const ratioPx2Pt = (96 / 72) * (viewportSize / 960)

// 获取边框配置
const getOutlineOption = (outline: SliderModels.PPTElementOutline): pptxgen.ShapeLineProps => {
  const c = formatColor(outline?.color || '#000000')

  return {
    color: c.color,
    transparency: (1 - c.alpha) * 100,
    width: (outline.width || 1) / ratioPx2Pt,
    dashType: outline.style ? (dashTypeMap[outline.style] as 'solid' | 'dash' | 'sysDot') : 'solid',
  }
}

// 判断是否为Base64图片地址
const isBase64Image = (url: string) => {
  const regex = /^data:image\/[^;]+;base64,/
  return url.match(regex) !== null
}

export const doPPTExport = (sliders: SliderModels.Slider[], fileName: string) => {
  return new Promise((resolve, reject) => {
    console.log('sliders', sliders)
    const pptx = new pptxgen()
    pptx.layout = 'LAYOUT_16x9'
    for (const slider of sliders) {
      const pptSlider = pptx.addSlide()
      if (slider.background) {
        pptSlider.background = { path: slider.background }
      }
      for (const element of slider.elements) {
        if (element.type === 'text') {
          const fixOffset = 30
          const textProps = formatHTML(element.content)
          const options: pptxgen.TextPropsOptions = {
            x: (element.x - fixOffset / 2) / ratioPx2Inch,
            y: (element.y + fixOffset / 2) / ratioPx2Inch,
            w: (element.width + fixOffset) / ratioPx2Inch,
            h: (element.height + fixOffset) / ratioPx2Inch,
            fontSize: 16 / ratioPx2Pt,
            fontFace: getFontFace().name,
            color: '#000000',
            valign: 'top',
            margin: 10 / ratioPx2Pt,
            paraSpaceBefore: 5 / ratioPx2Pt,
            lineSpacingMultiple: 1.5 / 1.25,
            autoFit: true,
          }
          pptSlider.addText(textProps, options)
        }
        if (element.type === 'image') {
          const options: pptxgen.ImageProps = {
            // path: element.src,
            sizing: {
              type: 'crop',
              w: element.width / ratioPx2Inch,
              h: element.height / ratioPx2Inch,
            },
            x: element.x / ratioPx2Inch,
            y: element.y / ratioPx2Inch,
            w: element.width / ratioPx2Inch,
            h: element.height / ratioPx2Inch,
          }
          if (isBase64Image(element.src)) {
            options.data = element.src
          } else {
            options.path = element.src
          }
          pptSlider.addImage(options)
        }
        if (element.type === 'shape') {
          const scale = {
            x: element.width / element.viewBox[0],
            y: element.height / element.viewBox[1],
          }
          const points = formatPoints(toPoints(element.path), scale)
          let fillColor = formatColor(element.fill)
          if (element.gradient) {
            const colors = element.gradient.colors
            const color1 = colors[0].color
            const color2 = colors[colors.length - 1].color
            const color = tinycolor.mix(color1, color2).toHexString()
            fillColor = formatColor(color)
          }
          const opacity = element.opacity === undefined ? 1 : element.opacity

          const options: pptxgen.ShapeProps = {
            x: element.x / ratioPx2Inch,
            y: element.y / ratioPx2Inch,
            w: element.width / ratioPx2Inch,
            h: element.height / ratioPx2Inch,
            fill: { color: fillColor.color, transparency: (1 - fillColor.alpha * opacity) * 100 },
            points,
          }
          // if (element.flipH) options.flipH = element.flipH
          // if (element.flipV) options.flipV = element.flipV
          // if (element.shadow) options.shadow = getShadowOption(element.shadow)
          if (element.outline?.width) options.line = getOutlineOption(element.outline)
          if (element.rotate) options.rotate = element.rotate
          /*           if (element.link) {
            const linkOption = getLinkOption(element.link)
            if (linkOption) options.hyperlink = linkOption
          } */

          pptSlider.addShape('custGeom' as pptxgen.ShapeType, options)
        }

        if (element.type === 'line') {
          const path = getLineElementPath(element)
          const points = formatPoints(toPoints(path))
          const { minX, maxX, minY, maxY } = getElementRange(element)
          const c = formatColor(element.color)

          const options: pptxgen.ShapeProps = {
            x: element.x / ratioPx2Inch,
            y: element.y / ratioPx2Inch,
            w: (maxX - minX) / ratioPx2Inch,
            h: (maxY - minY) / ratioPx2Inch,
            line: {
              color: c.color,
              transparency: (1 - c.alpha) * 100,
              width: element.width / ratioPx2Pt,
              dashType: dashTypeMap[element.style] as 'solid' | 'dash' | 'sysDot',
              beginArrowType: element.points[0] ? 'arrow' : 'none',
              endArrowType: element.points[1] ? 'arrow' : 'none',
            },
            points,
          }
          // if (element.shadow) options.shadow = getShadowOption(element.shadow)

          pptSlider.addShape('custGeom' as pptxgen.ShapeType, options)
        }

        if (element.type === 'video') {
          const options: pptxgen.MediaProps = {
            x: element.x / ratioPx2Inch,
            y: element.y / ratioPx2Inch,
            w: element.width / ratioPx2Inch,
            h: element.height / ratioPx2Inch,
            path: element.src,
            type: element.type,
          }
          if (element.type === 'video' && element.poster) options.cover = element.poster
          const extMatch = element.src.match(/\.([a-zA-Z0-9]+)(?:[\?#]|$)/)
          if (extMatch && extMatch[1]) options.extn = extMatch[1]
          else if (element.ext) options.extn = element.ext

          const videoExts = ['mp4', 'mov',]
          const upperCaseVideoExts = videoExts.map((ext) => ext.toUpperCase())
          if (options.extn && [...videoExts, ...upperCaseVideoExts,].includes(options.extn)) {
            pptSlider.addMedia(options)
          }
        }
      }
    }
    setTimeout(() => {
      pptx
        .writeFile({ fileName: `${fileName}.pptx` })
        .then(() => {
          resolve(undefined)
        })
        .catch(() => {
          // message.error('导出失败')
          reject()
        })
    }, 200)
  })
}
